import ProjectSummary from '../components/ProjectSummary';
import React, { useEffect, useState } from 'react';
import authFetch from '../api';
import WeatherPanel from '../components/WeatherPanel';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AdminHome() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
    // Check if user is logged in
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      console.log("No JWT token found, redirecting to login");
      window.location.href = "/login";
      return;
    }
      setLoading(true);
      try {
        // Get user from localStorage (this should work since login stores it)
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        } else {
          console.error('No user found in localStorage');
          setLoading(false);
          return;
        }

        // Fetch projects based on user role
        const projectsRes = await authFetch('/projects/assigned');
        console.log('🔍 AdminHome - Raw response:', projectsRes);
        
        // Handle both direct response and axios response format
        let projectsData;
        if (projectsRes && projectsRes.data) {
          // Axios response format
          projectsData = projectsRes.data;
          console.log('🔍 AdminHome - Using res.data (axios format)');
        } else {
          // Direct response format
          projectsData = projectsRes;
          console.log('🔍 AdminHome - Using direct response');
        }
        
        console.log('🔍 AdminHome - Projects data received:', projectsData);
        console.log('🔍 AdminHome - Projects data type:', typeof projectsData);
        console.log('🔍 AdminHome - Is array?', Array.isArray(projectsData));
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        console.log('🔍 AdminHome - Projects state set to:', Array.isArray(projectsData) ? projectsData : []);

        // Fetch users
        const usersRes = await authFetch('/users');
        console.log('🔍 AdminHome - Users raw response:', usersRes);
        
        // Handle both direct response and axios response format for users
        let usersData;
        if (usersRes && usersRes.data) {
          usersData = usersRes.data;
          console.log('🔍 AdminHome - Using users res.data (axios format)');
        } else {
          usersData = usersRes;
          console.log('🔍 AdminHome - Using users direct response');
        }
        
        console.log('🔍 AdminHome - Users data received:', usersData);
        setUsers(Array.isArray(usersData.users) ? usersData.users : Array.isArray(usersData) ? usersData : []);

        // Fetch workers
        const workersRes = await authFetch('/workers');
        console.log('🔍 AdminHome - Workers raw response:', workersRes);
        
        // Handle both direct response and axios response format for workers
        let workersData;
        if (workersRes && workersRes.data) {
          workersData = workersRes.data;
          console.log('🔍 AdminHome - Using workers res.data (axios format)');
        } else {
          workersData = workersRes;
          console.log('🔍 AdminHome - Using workers direct response');
        }
        
        console.log('🔍 AdminHome - Workers data received:', workersData);
        setWorkers(Array.isArray(workersData) ? workersData : []);

        // Fetch notifications
        try {
          const notifRes = await authFetch.get('/notifications/all');
          setNotifications(Array.isArray(notifRes.data) ? notifRes.data : []);
        } catch (notifError) {
          console.log('Notifications API failed:', notifError);
          setNotifications([]);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  console.log('🔍 AdminHome Debug:', { loading, user: !!user, userData: user });
  console.log('🔍 AdminHome Stats Debug:', { 
    projectsCount: projects.length, 
    usersCount: users.length, 
    workersCount: workers.length,
    activeWorkersCount: workers.filter(w => w.status === 'Active').length
  });
  
  if (loading || !user) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center pt-16">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('admin.dashboard')}</h1>
            <p className="text-gray-600 mt-2">{t('admin.loadingDashboard')}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('admin.loadingData')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show notification block only if there are unread notifications
  const unreadNotifications = notifications.filter(n => !n.read);
  const hasNotifications = unreadNotifications.length > 0;
  const latestNotification = hasNotifications ? unreadNotifications[0] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.dashboard')}</h1>
          <p className="text-gray-600 mt-2">{t('admin.welcomeMessage')}</p>
        </div>
      </div>

      {/* User Welcome Card */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="p-6">
          <div className="flex items-center gap-6">
            <img
              src={user.avatar ? user.avatar : 'http://localhost:5001/images/iesavatar.jpg'}
              alt="avatar"
              className="w-16 h-16 rounded-full border-4 border-blue-100 shadow object-cover"
              onError={(e) => {
                e.target.src = 'http://localhost:5001/images/iesavatar.jpg';
              }}
            />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1 whitespace-nowrap">
                {t('admin.welcomeUser')}, {user.name ? user.name : 'Потребител'}
              </h2>
              <p className="text-gray-600 text-sm">
                {user.role ? user.role : t('admin.userRole')} · {user.email ? user.email : t('admin.noEmail')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Block (dynamic) */}
      {hasNotifications && (
        <Link to="/admin/dashboard/notifications" className="block">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 hover:bg-yellow-100 hover:border-yellow-300 transition-all duration-200 cursor-pointer group">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400 group-hover:text-yellow-500 transition-colors duration-200" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800 group-hover:text-yellow-900 transition-colors duration-200">{t('admin.notification')}</h3>
                <p className="text-sm text-yellow-700 mt-1 group-hover:text-yellow-800 transition-colors duration-200">{latestNotification.message}</p>
              </div>
              <div className="text-xs text-yellow-600 group-hover:text-yellow-700 transition-colors duration-200">
                {new Date(latestNotification.createdAt).toLocaleString('bg-BG')}
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Stats Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <Link to="/admin/dashboard/projects" className="block">
          <div className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group border border-gray-200">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors duration-200">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{t('admin.totalProjects')}</p>
                  <p className="text-2xl font-semibold text-gray-900">{projects.length}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {projects.filter(p => p.status === 'On Track').length} {t('admin.activeProjects')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/admin/dashboard/users" className="block">
          <div className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group border border-gray-200">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-200">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{t('admin.totalEmployees')}</p>
                  <p className="text-2xl font-semibold text-gray-900">{users.length}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {users.filter(u => u.role === 'Admin' || u.role === 'SuperAdmin').length} {t('admin.administrators')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/admin/dashboard/workers" className="block">
          <div className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group border border-gray-200">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center group-hover:bg-yellow-200 transition-colors duration-200">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{t('admin.activeWorkers')}</p>
                  <p className="text-2xl font-semibold text-gray-900">{workers.filter(w => w.status === 'Active').length}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.ofTotal')} {workers.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: WeatherPanel */}
        <div className="lg:col-span-1">
          <WeatherPanel />
        </div>
        
        {/* Right: ProjectSummary */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow overflow-hidden h-full border border-gray-200">
            <div className="p-6 h-full flex flex-col">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('admin.projectSummary')}</h3>
              <div className="flex-1">
                {console.log('🔍 AdminHome - About to render ProjectSummary with projects:', projects)}
                <ProjectSummary projects={projects} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
